<template>
    <div class="enps-dashboard">
        <EnpsPeriodPills :year="selectedYear" :months="selectedMonths" @change="onPeriodChange">
            <!-- Переключатель отделов — только администратору: остальных бэкенд сам запирает в
                 их отделе, и селект с единственным недоступным вариантом только путал бы.
                 VSelect (custom-select) — тот же компонент, что в админ-модалках и смене
                 исполнителя; сброс крестиком возвращает «Все отделы» (placeholder).

                 На вкладке «Авторы» селект скрыт: он означает отдел ИСПОЛНИТЕЛЯ оценки, и в
                 разрезе по авторам такое сужение отвечало бы на вопрос, которого никто не
                 задавал. Бэкенд ?department= там тоже не читает. -->
            <template #aside>
                <VSelect
                    v-if="$_isAdmin && activeTab !== 'authors'"
                    :options="departmentOptions"
                    placeholder="Все отделы"
                    v-model="selectedDepartment"
                    @input="onDepartmentChange"
                ></VSelect>
            </template>
        </EnpsPeriodPills>

        <!-- Вкладки разводят уровни, которые раньше были перемешаны на одной странице:
             командная аналитика, разбор исполнителей, собственные оценки и (администратору)
             разрез по авторам оценок. Текстовые табы с подчёркиванием, а не зелёные кнопки:
             кнопка обещает действие, вкладка — переход. -->
        <div class="enps-dashboard__tabs">
            <button
                v-for="tab in tabs"
                :key="tab.key"
                type="button"
                class="enps-dashboard__tab"
                :class="{ 'enps-dashboard__tab_active': activeTab === tab.key }"
                @click="selectTab(tab.key)"
            >
                {{ tab.label }}
            </button>
        </div>

        <!-- Ошибка запроса сводки: честный блок вместо контента — нулевые заглушки под ним
             читались бы как валидные данные. «Повторить» дёргает тот же fetchSummary с
             текущими фильтрами. -->
        <div v-if="loadError" class="enps-dashboard__error">
            <div>Не удалось загрузить данные</div>
            <button type="button" class="enps-dashboard__error-retry" @click="fetchSummary">Повторить</button>
        </div>

        <!-- Контент не прячется через v-if/v-show на время загрузки: v-if размонтировал бы
             виджеты, и тренд-графики перезапрашивались бы, хотя от фильтра периода не зависят,
             а в display:none Chart.js рендерится в нулевой размер. Вместо этого поверх
             смонтированного контента ложится полупрозрачный оверлей со спиннером. -->
        <div v-else class="enps-dashboard__content">
            <div v-if="isLoading" class="enps-dashboard__loading-overlay">
                <VSpinner></VSpinner>
            </div>

            <!-- Отдел и счётчик «оценённых из закрытых» считаются по исполнителям: на вкладке
                 авторов они относились бы к другой совокупности, чем цифры под ними, поэтому
                 там от подписи остаётся только период. -->
            <div class="enps-dashboard__applied-period">
                {{ periodLabel }}
                <template v-if="activeTab !== 'authors'">
                    <template v-if="scopeLabel"> · {{ scopeLabel }}</template> · {{ ratedShare.ratedCount }} оценённых
                    из {{ ratedShare.closedCount }} закрытых
                </template>
            </div>

            <!-- ═══ Сводка: командный уровень ═══ -->
            <template v-if="activeTab === 'summary'">
                <EnpsOverallStats
                    :overall="overall"
                    :rated-share="ratedShare"
                    :top-ratings-share="topRatingsShare"
                ></EnpsOverallStats>

                <div class="enps-dashboard__charts">
                    <EnpsMonthlyTrendChart :department="departmentParam"></EnpsMonthlyTrendChart>
                    <EnpsRatedShareTrendChart :department="departmentParam"></EnpsRatedShareTrendChart>
                </div>

                <div class="enps-dashboard__charts">
                    <EnpsResolutionTimeChart :buckets="resolutionTimeBuckets"></EnpsResolutionTimeChart>
                    <EnpsProblemCategories :categories="problemCategories"></EnpsProblemCategories>
                </div>

                <EnpsDepartmentChart
                    class="enps-dashboard__section"
                    :department-breakdown="departmentBreakdown"
                    :period="datePeriodValue"
                ></EnpsDepartmentChart>

                <!-- Конкретные случаи сразу под разрезом по подразделениям заявителей: увидев
                     просевшее подразделение, логично тут же посмотреть, что именно там
                     произошло, и перейти в обращение. -->
                <EnpsLowRatingsList class="enps-dashboard__section" :ratings="recentLowRatings"></EnpsLowRatingsList>
            </template>

            <!-- ═══ Категории: график + таблица категорий обращений, вынесены из «Сводки»
                 отдельной вкладкой — это были два самых тяжёлых блока первого экрана. Данные не
                 перезапрашиваются: categoryChart/categoryList приходят тем же fetchSummary, что
                 и «Сводка» (один ответ /v1/enps/dashboard на весь дашборд), поэтому переключение
                 на эту вкладку не бьёт в сеть. -->
            <template v-else-if="activeTab === 'categories'">
                <EnpsCategoryChart
                    class="enps-dashboard__section"
                    :category-chart="categoryChart"
                    :period="datePeriodValue"
                ></EnpsCategoryChart>

                <!-- Карточный контейнер — без него таблица выглядела бы «голой» сразу под
                     графиком с тенью; стиль — тот же набор свойств, что у .enps-dashboard__split-list. -->
                <div class="enps-dashboard__card">
                    <EnpsCategoryModeTable
                        :category-list="categoryList"
                        :period="datePeriodValue"
                        :department="departmentParam"
                    ></EnpsCategoryModeTable>
                </div>
            </template>

            <!-- ═══ Исполнители: рейтинг слева, карточка выбранного справа ═══ -->
            <template v-else-if="activeTab === 'executors'">
                <div class="enps-dashboard__split">
                    <div class="enps-dashboard__split-list">
                        <EnpsRankingTable
                            :ranking="ranking"
                            :selected-id="selectedExecutor && selectedExecutor.accountId"
                            @select-executor="onSelectExecutor"
                        ></EnpsRankingTable>
                    </div>
                    <EnpsRatingsPanel
                        :subject="selectedExecutor"
                        :period="datePeriodValue"
                        :department="departmentParam"
                    ></EnpsRatingsPanel>
                </div>
            </template>

            <!-- ═══ Авторы (только администратору): кто ставит оценки ═══
                 Та же раскладка, что у исполнителей, но разрез зеркальный — не «кого оценили»,
                 а «кто оценил». Список грузит себя сам и сам же выбирает первую строку, поэтому
                 selectedAuthor приходит из его события, а не из Vuex-сводки. department не
                 передаётся: у разреза по авторам его нет ни на фронте, ни на бэкенде. -->
            <template v-else-if="activeTab === 'authors'">
                <div class="enps-dashboard__split">
                    <div class="enps-dashboard__split-list">
                        <EnpsAuthorTable
                            :period="datePeriodValue"
                            :selected-id="selectedAuthor && selectedAuthor.accountId"
                            @select-author="onSelectAuthor"
                        ></EnpsAuthorTable>
                    </div>
                    <EnpsRatingsPanel
                        :subject="selectedAuthor"
                        role="author"
                        :period="datePeriodValue"
                    ></EnpsRatingsPanel>
                </div>
            </template>

            <!-- ═══ Мои оценки: только собственные ═══
                 Та же панель, что в разборе исполнителей, но для себя: графики + полный список
                 «Все оценки» с фильтром по баллам (низкие смотрятся чипами ★1/★2 — отдельный
                 виджет «Мои низкие оценки» упразднён как дубль). department не передаётся
                 намеренно: свои показатели не должны пустеть от того, что администратор
                 переключил отдел. -->
            <template v-else>
                <EnpsRatingsPanel
                    class="enps-dashboard__my-panel"
                    :subject="selfExecutor"
                    :period="datePeriodValue"
                ></EnpsRatingsPanel>
            </template>
        </div>
    </div>
</template>
<script>
// Components
import VSelect from '../../components/VSelect/VSelect.vue';
import VSpinner from '@/views/components/VProgress/VSpinner.vue';
import {
    EnpsOverallStats,
    EnpsRankingTable,
    EnpsCategoryChart,
    EnpsCategoryModeTable,
    EnpsDepartmentChart,
    EnpsResolutionTimeChart,
    EnpsMonthlyTrendChart,
    EnpsPeriodPills,
    EnpsRatingsPanel,
    EnpsAuthorTable,
    EnpsRatedShareTrendChart,
    EnpsProblemCategories,
    EnpsLowRatingsList,
} from './components';

// Other
import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import http from '@/js/http';
import { buildPeriodValue, formatPeriodLabel, parsePeriodSelection } from '@/js/enpsPeriod';

const EnpsDashboard = namespace('enpsDashboard');

// adminOnly — вкладка «Авторы»: она собирает комментарии сотрудника по человеку, а не по
// обращению, и это уже профиль автора, а не рабочая статистика. Бэкенд закрывает её тем же
// правилом (role.admin поверх role.executor), фронт лишь не рисует то, что всё равно вернёт 403.
const TABS = [
    { key: 'summary', label: 'Сводка' },
    { key: 'categories', label: 'Категории' },
    { key: 'executors', label: 'Исполнители' },
    { key: 'authors', label: 'Авторы', adminOnly: true },
    { key: 'my', label: 'Мои оценки' },
];

@Component({
    components: {
        VSelect,
        VSpinner,
        EnpsOverallStats,
        EnpsRankingTable,
        EnpsCategoryChart,
        EnpsCategoryModeTable,
        EnpsDepartmentChart,
        EnpsResolutionTimeChart,
        EnpsMonthlyTrendChart,
        EnpsPeriodPills,
        EnpsRatingsPanel,
        EnpsAuthorTable,
        EnpsRatedShareTrendChart,
        EnpsProblemCategories,
        EnpsLowRatingsList,
    },
})
class EnpsDashboardPage extends Vue {
    @EnpsDashboard.State isLoading;
    @EnpsDashboard.State loadError;
    @EnpsDashboard.State ranking;
    @EnpsDashboard.State overall;
    @EnpsDashboard.State categoryChart;
    @EnpsDashboard.State categoryList;
    @EnpsDashboard.State ratedShare;
    @EnpsDashboard.State topRatingsShare;
    @EnpsDashboard.State departmentBreakdown;
    @EnpsDashboard.State resolutionTimeBuckets;
    @EnpsDashboard.State problemCategories;
    @EnpsDashboard.State recentLowRatings;
    @EnpsDashboard.Action FETCH_SUMMARY;

    // Выбор таблеток: один год и произвольный набор месяцев внутри него. Хранится здесь, а не в
    // EnpsPeriodPills, потому что из него же собирается datePeriodValue — единственное значение,
    // которое уходит в запрос, в адресную строку и дочерним компонентам через :period.
    selectedYear = new Date().getFullYear();
    selectedMonths = [new Date().getMonth() + 1];
    datePeriodValue = '';

    // Отдел, выбранный администратором (null — все отделы: так vue-select показывает
    // placeholder и отдаёт значение после сброса крестиком). У не-админа всегда null — его
    // запирает в своём отделе бэкенд (resolveDepartmentScope), фронту его отдел знать не
    // обязательно.
    selectedDepartment = null;
    departmentOptions = [];

    // 'summary' | 'executors' | 'authors' | 'my' — активная вкладка; пишется в ?tab=, чтобы
    // ссылку на конкретный разрез можно было переслать. Период общий для всех вкладок, отдел —
    // для всех, кроме «Авторов».
    activeTab = 'summary';

    // Исполнитель, открытый в панели справа на вкладке «Исполнители» (строка рейтинга).
    selectedExecutor = null;

    // Автор, открытый в панели справа на вкладке «Авторы». В отличие от исполнителя приходит
    // не из Vuex-сводки, а событием списка: тот грузит себя сам (см. EnpsAuthorTable).
    selectedAuthor = null;

    // Вкладки, доступные текущему пользователю: «Авторы» — только администратору.
    get tabs() {
        return TABS.filter((tab) => !tab.adminOnly || this.$_isAdmin);
    }

    get periodLabel() {
        return formatPeriodLabel(this.datePeriodValue);
    }

    // Выбранный отдел в виде значения для запросов/пропсов ('' — все): null удобен селекту,
    // но в query-параметрах и ключах кэшей ожидается строка.
    get departmentParam() {
        return this.selectedDepartment || '';
    }

    // Сам пользователь в формате строки рейтинга — для панели на вкладке «Мои оценки».
    get selfExecutor() {
        if (!this.$_account?.id) {
            return null;
        }

        return {
            accountId: this.$_account.id,
            displayName: this.$_account.displayName,
        };
    }

    // Подпись текущего скоупа под фильтрами: админу — выбранный отдел, остальным — их
    // собственный (если LDAP его заполнил; без отдела ничего не показываем, бэкенд и так
    // скоупит по «безотдельному» сегменту).
    get scopeLabel() {
        if (this.$_isAdmin) {
            return this.selectedDepartment || 'все отделы';
        }

        return this.$_account?.department || '';
    }

    // После смены периода/отдела рейтинг пересчитывается: если открытый исполнитель остался в
    // нём — оставляем (пусть разбор не сбрасывается лишний раз), иначе выбираем первого, чтобы
    // панель не показывала человека, которого в таблице слева больше нет.
    @Watch('ranking')
    onRankingChange() {
        const rated = this.ranking?.rated ?? [];
        const selectedId = this.selectedExecutor?.accountId;

        if (selectedId && rated.some((row) => row.accountId === selectedId)) {
            return;
        }

        this.selectedExecutor = rated[0] ?? null;
    }

    onSelectExecutor(executor) {
        this.selectedExecutor = executor;
    }

    onSelectAuthor(author) {
        this.selectedAuthor = author;
    }

    selectTab(tab) {
        if (this.activeTab === tab) {
            return;
        }

        this.activeTab = tab;
        this.replaceQueryInUrl();
    }

    onPeriodChange({ year, months }) {
        const periodValue = buildPeriodValue(year, months);

        // Повторный клик по уже выбранному месяцу/кварталу — не изменение: без раннего выхода
        // он оборачивался бы $router.replace на тот же URL (NavigationDuplicated) и лишним
        // рефетчем всей сводки.
        if (year === this.selectedYear && periodValue === this.datePeriodValue) {
            return;
        }

        this.selectedYear = year;
        this.selectedMonths = months;
        this.datePeriodValue = periodValue;

        this.replaceQueryInUrl();
        this.fetchSummary();
    }

    onDepartmentChange() {
        this.replaceQueryInUrl();
        this.fetchSummary();
    }

    fetchSummary() {
        this.FETCH_SUMMARY({ period: this.datePeriodValue, department: this.departmentParam });
    }

    replaceQueryInUrl() {
        const query = { period: this.datePeriodValue };

        // Значения по умолчанию («все отделы», вкладка «Сводка») в URL не пишем — они и так
        // подразумеваются, а пересылаемые ссылки короче.
        if (this.departmentParam) {
            query.department = this.departmentParam;
        }
        if (this.activeTab !== 'summary') {
            query.tab = this.activeTab;
        }

        // Отклонённый replace на фактически тот же URL (NavigationDuplicated) — штатная
        // ситуация vue-router 3, а не ошибка навигации, глушим её молча.
        this.$router
            .replace({
                name: 'enps-dashboard',
                query,
            })
            .catch(() => {});
    }

    // Период из адресной строки восстанавливается на таблетках, только если он выражается
    // целыми месяцами одного года (parsePeriodSelection). Произвольный диапазон из ссылки,
    // сохранённой до перехода на таблетки, показать нечем — такая ссылка открывает дашборд на
    // периоде по умолчанию (текущий месяц), а не молча с таблетками, не совпадающими с цифрами.
    initPeriod() {
        const selection = parsePeriodSelection(this.$route.query.period);

        // Дефолт (текущий месяц) выставляется явно, а не остаётся от прежнего состояния:
        // initFromRoute вызывается и повторно (watcher $route), и очистка адресной строки
        // должна возвращать дашборд к тому же виду, что и свежий заход без параметров.
        this.selectedYear = selection ? selection.year : new Date().getFullYear();
        this.selectedMonths = selection ? selection.months : [new Date().getMonth() + 1];
        this.datePeriodValue = buildPeriodValue(this.selectedYear, this.selectedMonths);
    }

    // Восстановление всего состояния страницы из адресной строки — и при создании, и при
    // смене query без пересоздания компонента (см. onRouteChange).
    initFromRoute() {
        this.initPeriod();

        // Сверяемся с ДОСТУПНЫМИ вкладками, а не со всем списком: ?tab=authors в пересланной
        // администратором ссылке не должен открывать у исполнителя вкладку, которая тут же
        // получит 403 — он попадает на «Сводку».
        this.activeTab = this.tabs.some((tab) => tab.key === this.$route.query.tab) ? this.$route.query.tab : 'summary';

        // Отдел из ссылки восстанавливаем только администратору: не-админу переключаться
        // некуда, бэкенд его параметр игнорирует, и показывать в селекте чужой отдел не из чего.
        if (this.$_isAdmin) {
            this.selectedDepartment = this.$route.query.department || null;
        }

        if (this.$route.query.period !== this.datePeriodValue) {
            this.replaceQueryInUrl();
        }

        this.fetchSummary();
    }

    // Клик по пункту меню «Оценка качества» делает переход на этот же маршрут без query —
    // компонент не пересоздаётся, и без watcher'а адресная строка очищалась бы, а UI оставался
    // на старом срезе. Сравнение query с текущим состоянием обязательно: собственный
    // replaceQueryInUrl тоже приводит сюда (в том числе сразу после ре-инициализации, когда он
    // возвращает ?period= в URL) — он должен увидеть совпадение и не зациклить
    // «replace → watcher → replace».
    @Watch('$route')
    onRouteChange() {
        const query = this.$route.query;
        const samePeriod = query.period === this.datePeriodValue;
        const sameTab = (query.tab || 'summary') === this.activeTab;
        const sameDepartment = (query.department || '') === this.departmentParam;

        if (samePeriod && sameTab && sameDepartment) {
            return;
        }

        this.initFromRoute();
    }

    created() {
        // Список отделов админскому селекту нужен один раз за жизнь компонента — в
        // повторную инициализацию по watcher'у его не тащим.
        if (this.$_isAdmin) {
            http.get('/enps/dashboard/departments')
                .then(({ data }) => {
                    this.departmentOptions = data;
                })
                .catch(() => {
                    // Селект без вариантов хуже, чем его отсутствие, но дашборд без
                    // переключателя всё ещё рабочий — падение списка не должно ронять страницу.
                    this.departmentOptions = [];
                });
        }

        this.initFromRoute();
    }
}

export default EnpsDashboardPage;
</script>
<style lang="scss">
.enps-dashboard {
    width: 100%;
    height: 100%;
    padding: 20px;
    overflow-y: auto;

    &__tabs {
        display: flex;
        align-items: center;
        gap: 22px;
        margin-bottom: 15px;
        border-bottom: 1px solid #e4eaef;
    }

    // Корп-оранжевый — интерфейсный акцент ENPS (см. enpsColors.js), зелёный здесь
    // зарезервирован за семантикой «хорошая оценка».
    &__tab {
        padding: 8px 2px 10px;
        border: none;
        border-bottom: 2px solid transparent;
        margin-bottom: -1px;
        background: none;
        color: #767676;
        font-size: 0.9375rem;
        cursor: pointer;
        transition: color 0.15s, border-color 0.15s;

        &:hover {
            color: #c05a1f;
        }

        // Тёмный текст + акцентное подчёркивание: цветного штриха достаточно, оранжевый текст
        // поверх него был бы вторым акцентом на том же элементе.
        &_active {
            color: #3c4858;
            border-bottom-color: #f1905b;
            font-weight: 600;
        }
    }

    &__applied-period {
        margin-bottom: 20px;
        color: #767676;
        font-size: 0.875rem;
    }

    // Блок ошибки загрузки — сдержанный серый, в духе enps-chart-placeholder: падение запроса
    // не повод для красного алармизма на всю страницу.
    &__error {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding: 60px 0;
        color: #767676;
    }

    // Текстовая ссылка тёмным оттенком акцента (ACCENT_DEEP из enpsColors.js) — как остальные
    // интерфейсные ссылки ENPS; кнопка-плашка обещала бы больше, чем простой повтор запроса.
    &__error-retry {
        padding: 0;
        border: none;
        background: none;
        color: #c05a1f;
        font-size: 0.9375rem;
        cursor: pointer;

        &:hover {
            text-decoration: underline;
        }
    }

    // Якорь для оверлея загрузки: контент под ним остаётся смонтированным (см. комментарий
    // в шаблоне).
    &__content {
        position: relative;
    }

    // Полупрозрачная пелена на время рефетча: перекрывает клики по устаревшему контенту и
    // показывает спиннер, не размонтируя виджеты. z-index — поверх canvas'ов Chart.js,
    // обёрнутых в собственный position: relative.
    &__loading-overlay {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 2;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: rgba(255, 255, 255, 0.6);
    }

    &__section {
        margin-bottom: 20px;
    }

    // Универсальный карточный контейнер — тот же набор свойств, что у &__split-list, но без
    // привязки к flex-раскладке «список/панель»: нужен там, где одиночный виджет (сейчас —
    // таблица категорий) иначе выглядел бы «голым» без карточки с тенью вокруг.
    &__card {
        margin-bottom: 20px;
        padding: 10px 5px;
        border-radius: 8px;
        background-color: var(--color-white);
        box-shadow: 0px 1px 10px rgba(88, 88, 88, 0.15);
    }

    &__charts {
        display: flex;
        margin-bottom: 20px;
        gap: 20px;
    }

    // Раскладка «список слева — карточка справа», общая для «Исполнителей» и «Авторов».
    &__split {
        display: flex;
        align-items: flex-start;
        gap: 20px;
    }

    // Панель своих показателей на «Моих оценках» — блок на всю ширину с отступом до списка
    // низких оценок (в раскладке «Исполнители» её растягивает flex, здесь она сама по себе).
    &__my-panel {
        margin-bottom: 20px;
    }

    // Карточный стиль в пару к панели справа — иначе таблица выглядела бы «голой» рядом с
    // карточкой с тенью.
    &__split-list {
        flex: 1.2;
        min-width: 0;
        padding: 10px 5px;
        border-radius: 8px;
        background-color: var(--color-white);
        box-shadow: 0px 1px 10px rgba(88, 88, 88, 0.15);

        .enps-ranking {
            margin-bottom: 0;
        }
    }
}
</style>
